import useAuthStore from 'store/auth'
import { accessControlChecker } from 'setups/controller'
import { projectClient } from 'constants/project'
import type { ClientUserTypeNames } from 'setups/clients'
import {
  MEMBERTABSNAVIGATION_FN,
  MEMBERMORETABSNAVIGATION_FN,
} from 'constants/profileNavMember'
import { MEMBERELLIPSISNAVIGATION_FN } from 'constants/profileEllipsisMember'
import { MEMBERMODALNAVIGATION_FN } from 'constants/profileModalMember'
import { MERCHANTELLIPSISNAVIGATION_FN } from 'constants/profileEllipsisMerchant'
import { MERCHANTMODALNAVIGATION_FN } from 'constants/profileModalMerchant'
import { MERCHANTTABSNAVIGATION_FN } from 'constants/profileNavMerchant'
import { SUBSQUADTABSNAVIGATION_FN } from 'constants/profileNavSubsquad'
import useMemberStore from 'store/member'
import useMerchantStore from 'store/merchant'

export function useCanUI() {
  const attributes = useAuthStore((state) => state.attributes)
  const userType = attributes?.attributes?.['custom:userType'] ?? ''
  const memberProfile = useMemberStore((state) => state.member)
  const memberthorStatus = memberProfile?.memberThorStatusName ?? ''
  const merchantProfile = useMerchantStore((state) => state.merchantProfile)
  const merchantThorStatus = merchantProfile[0]?.memberThorStatus ?? ''

  const memberTabsNavigation = MEMBERTABSNAVIGATION_FN(
    userType,
    'member',
    memberthorStatus
  )
  const memberMoreTabsNavigation = MEMBERMORETABSNAVIGATION_FN(
    userType,
    'member',
    memberthorStatus
  )
  const memberEllipsisNavigation = MEMBERELLIPSISNAVIGATION_FN(
    userType,
    'member',
    memberthorStatus
  )
  const memberModalNavigation = MEMBERMODALNAVIGATION_FN(
    userType,
    'member',
    memberthorStatus
  )
  const merchantEllipsisNavigation = MERCHANTELLIPSISNAVIGATION_FN(
    userType,
    'merchant',
    merchantThorStatus
  )
  const merchantModalNavigation = MERCHANTMODALNAVIGATION_FN(
    userType,
    'merchant',
    merchantThorStatus
  )
  const merchantTabsNavigation = MERCHANTTABSNAVIGATION_FN(
    userType,
    'merchant',
    merchantThorStatus
  )
  const subsquadTabsNavigation = SUBSQUADTABSNAVIGATION_FN(
    userType,
    'subsquad',
    ''
  )

  const can = accessControlChecker(
    projectClient,
    userType as ClientUserTypeNames<typeof projectClient>
  )

  // MEMBER

  const isMemberLookupCreateMember = can('member', 'lookup', 'create member')

  const isMemberLookupCreateDRDMember = can(
    'member',
    'lookup',
    'create drd member'
  )

  const isMemberTransactionDownload = can(
    'member',
    'transaction',
    'download transaction'
  )

  const isMemberCommsViewEmail = can('member', 'communication', 'view email')

  const isMemberCommsResendEmail = can(
    'member',
    'communication',
    'resend email'
  )

  const isMemberCommsEditEmail = can('member', 'communication', 'edit email')

  const isMemberCommsNZViewEmail = can(
    'member',
    'communication main nz',
    'view email'
  )

  const isMemberCommsNZResendEmail = can(
    'member',
    'communication main nz',
    'resend email'
  )

  const isMemberCommsNZEditEmail = can(
    'member',
    'communication main nz',
    'edit email'
  )

  const isMemberVoucherUpdateStatus = can(
    'member',
    'voucher',
    'update voucher status'
  )

  const isMemberMembershipAdd = can('member', 'membership', 'add membership') // to be confirmed

  const isMemberMembershipEnd = can('member', 'membership', 'end membership')

  const isMemberIdentificationMerge = can(
    'member',
    'identification',
    'merge identifiers'
  )

  const isMemberIdentificationUpdate = can(
    'member',
    'identification',
    'update identifier status'
  )

  const isMemberIdentificationUpdateGeneric = can(
    'member',
    'identification generic',
    'update identifier status'
  )

  const isMemberIdentificationHampstaAddReplaceCard = can(
    'member',
    'identification hampsta',
    'add/replace card'
  )

  const isMemberIdentificationHampstaUpdateStatus = can(
    'member',
    'identification hampsta',
    'update identifier status'
  )

  const isMemberIdentificationGeneric = can(
    'member',
    'identification generic',
    'update identifier status'
  )

  const isMemberBankAccountAddBank = can(
    'member',
    'bank accounts',
    'add bank account'
  )

  const isMemberBankAccountUpdateBank = can(
    'member',
    'bank accounts',
    'update bank account'
  )

  const isMemberDirectPaymentAddPaymentInst = can(
    'member',
    'direct payments',
    'add payment instruction'
  )

  const isMemberDirectPaymentUpdatePaymentInst = can(
    'member',
    'direct payments',
    'update payment instruction'
  )

  const isMemberDirectPaymentSkipPaymantInst = can(
    'member',
    'direct payments',
    'skip payment instruction'
  )

  const isMemberDirectPaymentCancelPaymantInst = can(
    'member',
    'direct payments',
    'cancel payment instruction'
  )

  const isMemberNotesAddNote = can('member', 'notes', 'add note')

  const isMemberNotesViewNote = can('member', 'notes', 'view note')

  const isMemberNotesUpdateNote = can('member', 'notes', 'update note')

  // MERCHANT

  const isMerchantLookupCreateMember = can(
    'merchant',
    'lookup',
    'create merchant'
  )

  const isMerchantTransactionDownload = can(
    'merchant',
    'transaction',
    'download transaction'
  )

  const isMerchantIdentificationUpdateStatus = can(
    'merchant',
    'identification',
    'update identifier status'
  )

  const isMerchantDeviceUpdateDevice = can(
    'merchant',
    'device',
    'update merchant device'
  )

  const isMerchantBankAccountUpdateBank = can(
    'merchant',
    'bank accounts',
    'update bank account'
  )

  // VOUCHER

  const isVoucherViewDuplicateVoucher = can(
    'voucher',
    'view voucher',
    'duplicate voucher'
  ) // to be confirmed

  // REPORTS

  const isReportReport2Download = can('reports', 'report 2', 'download report')

  const isReportReport13Download = can(
    'reports',
    'report 13',
    'download report'
  )

  const isReportReport19Download = can(
    'reports',
    'report 19',
    'download report'
  )

  const isReportVoucherRedemptCountReport = can(
    'reports',
    'voucher redemption count report',
    'download report'
  )

  return {
    memberTabsNavigation,
    memberMoreTabsNavigation,
    memberEllipsisNavigation,
    memberModalNavigation,
    merchantEllipsisNavigation,
    merchantModalNavigation,
    merchantTabsNavigation,
    subsquadTabsNavigation,
    // MEMBER
    isMemberLookupCreateMember,
    isMemberLookupCreateDRDMember,
    isMemberTransactionDownload,
    isMemberCommsViewEmail,
    isMemberCommsResendEmail,
    isMemberCommsEditEmail,
    isMemberCommsNZViewEmail,
    isMemberCommsNZResendEmail,
    isMemberCommsNZEditEmail,
    isMemberVoucherUpdateStatus,
    isMemberMembershipAdd,
    isMemberMembershipEnd,
    isMemberIdentificationMerge,
    isMemberIdentificationUpdate,
    isMemberIdentificationUpdateGeneric,
    isMemberIdentificationHampstaAddReplaceCard,
    isMemberIdentificationHampstaUpdateStatus,
    isMemberIdentificationGeneric,
    isMemberBankAccountAddBank,
    isMemberBankAccountUpdateBank,
    isMemberDirectPaymentAddPaymentInst,
    isMemberDirectPaymentUpdatePaymentInst,
    isMemberDirectPaymentSkipPaymantInst,
    isMemberDirectPaymentCancelPaymantInst,
    isMemberNotesAddNote,
    isMemberNotesViewNote,
    isMemberNotesUpdateNote,
    // MERCHANT
    isMerchantLookupCreateMember,
    isMerchantTransactionDownload,
    isMerchantIdentificationUpdateStatus,
    isMerchantDeviceUpdateDevice,
    isMerchantBankAccountUpdateBank,
    // VOUCHER
    isVoucherViewDuplicateVoucher,
    // REPORTS
    isReportReport2Download,
    isReportReport13Download,
    isReportReport19Download,
    isReportVoucherRedemptCountReport,
  }
}
